import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import LangLink from '../common/LangLink';
import { userService } from '../../services/api';
import { Loader2, CheckCircle2, XCircle, ShoppingBag, ArrowLeft } from 'lucide-react';

const CheckoutReturn = () => {
    const { language } = useLanguage();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session_id');

    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // 'complete' | 'open' | 'expired'
    const [customerEmail, setCustomerEmail] = useState(null);

    useEffect(() => {
        if (!sessionId) {
            setLoading(false);
            return;
        }

        userService.getSessionStatus(sessionId)
            .then(data => {
                setStatus(data.status);
                setCustomerEmail(data.customer_email);
            })
            .catch(() => {
                setStatus('error');
            })
            .finally(() => setLoading(false));
    }, [sessionId]);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-blue-500 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium">
                        {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
                    </p>
                </div>
            </div>
        );
    }

    if (status === 'complete') {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-md w-full text-center">
                    <div className="bg-white dark:bg-[#121212] rounded-3xl border border-gray-200 dark:border-white/10 p-8 shadow-xl">
                        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                        </div>
                        <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
                            {language === 'fr' ? 'Paiement confirmé !' : 'Payment confirmed!'}
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                            {language === 'fr'
                                ? 'Votre abonnement est maintenant actif. Un reçu a été envoyé à votre adresse courriel.'
                                : 'Your subscription is now active. A receipt has been sent to your email address.'}
                            {customerEmail && (
                                <span className="block mt-1 font-medium text-gray-700 dark:text-gray-300">
                                    {customerEmail}
                                </span>
                            )}
                        </p>
                        <div className="flex flex-col gap-3">
                            <LangLink
                                to="/dashboard/account/orders"
                                className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-green-500/25 flex items-center justify-center gap-2"
                            >
                                <ShoppingBag className="w-4 h-4" />
                                {language === 'fr' ? 'Voir mes commandes' : 'View my orders'}
                            </LangLink>
                            <LangLink
                                to="/dashboard"
                                className="w-full py-3 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 font-bold rounded-xl transition hover:bg-gray-200 dark:hover:bg-white/15 flex items-center justify-center gap-2"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                {language === 'fr' ? 'Retour au tableau de bord' : 'Back to dashboard'}
                            </LangLink>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error or expired
    return (
        <div className="min-h-[60vh] flex items-center justify-center px-4">
            <div className="max-w-md w-full text-center">
                <div className="bg-white dark:bg-[#121212] rounded-3xl border border-gray-200 dark:border-white/10 p-8 shadow-xl">
                    <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                        <XCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-2">
                        {language === 'fr' ? 'Paiement non complété' : 'Payment not completed'}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                        {status === 'open'
                            ? (language === 'fr'
                                ? 'Le paiement est toujours en cours. Si vous avez quitté par erreur, veuillez réessayer.'
                                : 'The payment is still in progress. If you left by mistake, please try again.')
                            : (language === 'fr'
                                ? 'La session de paiement a expiré ou une erreur est survenue. Veuillez réessayer.'
                                : 'The payment session has expired or an error occurred. Please try again.')
                        }
                    </p>
                    <LangLink
                        to="/levels"
                        className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {language === 'fr' ? 'Retour aux abonnements' : 'Back to subscriptions'}
                    </LangLink>
                </div>
            </div>
        </div>
    );
};

export default CheckoutReturn;
